import { useEffect, useReducer } from "react";
import { WmButton } from "@watermarkinsights/ripple-react";
import translate from "../../../i18n/translate";
import { downloadPDF } from "../../common/helper";
import { sendRequest, sendRequestWithPayload } from "../../../common/http";
import { useIntl } from "react-intl";
import MirageStateLink from "@/mirage/MirageStateLink";

interface TokenResultSet {
  token: string;
}

interface ExportResultSet {
  pdf_file_bytes: string;
}
export interface HeaderProps {
  reportingYear: string;
  showDownloadBtn: boolean;
}

interface HeaderAction {
  type: string;
  token: string | null;
  data: boolean;
}

const defaultHeaderAction = {
  type: "",
  token: "",
  data: false
};

interface HeaderState {
  token: string | null;
  isExportInProgress: boolean;
}

const reducer = (state: HeaderState, action: HeaderAction) => {
  switch (action.type) {
    case "setToken":
      return { ...state, token: action.token };
    case "setExportInProgress":
      return { ...state, isExportInProgress: action.data };
    default:
      return state;
  }
};

export default function Header({
  reportingYear,
  showDownloadBtn
}: HeaderProps): JSX.Element {
  const initialState = {
    token: null,
    isExportInProgress: false
  };

  const header = useIntl().formatMessage({
    id: "app.FacultyAccomplishments"
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  function exportToPdf() {
    let facultyHtml = document.getElementById("facultyHtml");
    let downloadText = document.querySelector(".download-text");
    if (downloadText) {
      downloadText.setAttribute("tabindex", "0");
      (downloadText as HTMLElement).focus();
    }

    if (facultyHtml) {
      let clone = facultyHtml.cloneNode(true);

      let headerSection = clone.firstChild;
      headerSection && clone.removeChild(headerSection);
      let wrapperMain = document.createElement("main");
      wrapperMain.classList.add("main-container");
      wrapperMain.appendChild(clone);

      let wrapperDiv = document.createElement("div");
      wrapperDiv.appendChild(wrapperMain);

      let formData = new FormData();
      formData.append("html", wrapperDiv.outerHTML);
      formData.append("header", encodeURIComponent(header));
      formData.append("sub_header", encodeURIComponent(reportingYear));
      formData.append("export_id", "accomplishments-export")
      formData.append("_csrf_token", state.token ? state.token : "");

      const applyResult = (result: ExportResultSet) => {
        downloadPDF(result.pdf_file_bytes, "Faculty_Accomplishments");
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      const onError = () => {
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      sendRequestWithPayload(
        {
          uri: "/export",
          body: formData
        },
        applyResult,
        onError
      );
    }
  }

  useEffect(() => {
    if (state.token && state.isExportInProgress) {
      exportToPdf();
    }
  }, [state.isExportInProgress, state.token]);

  useEffect(() => {
    if (state.isExportInProgress) {
      sendRequest(
        {
          uri: "/export/token",
          camelize: true
        },
        (result: TokenResultSet) => {
          dispatch({
            ...defaultHeaderAction,
            type: "setToken",
            token: result.token
          });
        }
      );
    }
  }, [state.isExportInProgress]);

  return (
    <>
      <section
        className="page-banner-container -mainheader -withbreadcrumbs only-breadcrumbs header-padding"
        id="page-header"
      >
        <div className="flex-row export-container">
          <div>
            <div>
              <span className="org-name uppercase">{reportingYear}</span>
            </div>
            <div className="identity-details">
              <div className="page-header -titleonly">
                <h2 id="main-heading" className="plan-title-header">{header}</h2>
              </div>
            </div>
          </div>
          {process.env.NODE_ENV === 'development' && process.env.MIRAGE_ENV === 'true' && (
            <div className="flex-row export-button">
              <MirageStateLink />
            </div>
          )}
          {showDownloadBtn && (
            <div className="flex-row export-button">
              {state.isExportInProgress && (
                <span className="download-text">
                  {translate(
                    "app.planParticipation.exportPdf.downloadReportText"
                  )}
                </span>
              )}
              <div className="button-container">
                <WmButton
                  button-type="secondary"
                  icon="f1da"
                  icon-size="20px"
                  onClick={() =>
                    dispatch({
                      ...defaultHeaderAction,
                      type: "setExportInProgress",
                      data: true
                    })
                  }
                  disabled={state.isExportInProgress}
                >
                  {translate("app.downloadHtml")}
                </WmButton>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
